import { Injectable, inject } from '@angular/core';
import { CanActivate, CanActivateChild, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { RolesService } from '../../services/roles.service';
import { Roles } from '../../interfaces/user.interface';

/**
 * Guard para proteger rotas com autenticação e autorização
 * Seguindo as regras do Angular: functional guards, injeção com inject()
 */
@Injectable({
    providedIn: 'root',
})
export class AuthGuard implements CanActivate, CanActivateChild {
    private readonly authService = inject(AuthService);
    private readonly rolesService = inject(RolesService);
    private readonly router = inject(Router);

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        return this.checkAuth(route, state);
    }

    canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        return this.checkAuth(childRoute, state);
    }

    /**
     * Verifica autenticação e autorização
     */
    private checkAuth(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        // Verificar se usuário está autenticado
        if (!this.authService.estaAutenticado()) {
            this.redirectToLogin(state.url);
            return of(false);
        }

        // Verificar roles se especificados na rota
        const requiredRoles = route.data?.['roles'] as Roles[];
        if (requiredRoles && requiredRoles.length > 0) {
            if (!this.rolesService.hasAnyRole(requiredRoles)) {
                this.redirectToUnauthorized();
                return of(false);
            }
        }

        // Verificar role mínimo se especificado
        const minimumRole = route.data?.['minimumRole'] as Roles;
        if (minimumRole && !this.rolesService.hasMinimumRole(minimumRole)) {
            this.redirectToUnauthorized();
            return of(false);
        }

        // Verificar acesso a feature específica
        const requiredFeature = route.data?.['feature'] as string;
        if (requiredFeature && !this.rolesService.canAccessFeature(requiredFeature)) {
            this.redirectToUnauthorized();
            return of(false);
        }

        return of(true);
    }

    /**
     * Redireciona para login
     */
    private redirectToLogin(returnUrl: string): void {
        this.router.navigate(['/auth/login'], {
            queryParams: { returnUrl },
        });
    }

    /**
     * Redireciona para página de não autorizado
     */
    private redirectToUnauthorized(): void {
        this.router.navigate(['/unauthorized']);
    }
}

/**
 * Functional guard para rotas administrativas
 */
export const adminGuard = () => {
    const authService = inject(AuthService);
    const rolesService = inject(RolesService);
    const router = inject(Router);

    if (!authService.estaAutenticado()) {
        router.navigate(['/auth/login']);
        return false;
    }

    if (!rolesService.hasAnyRole([Roles.SYSTEM_ADMIN, Roles.ADMIN])) {
        router.navigate(['/unauthorized']);
        return false;
    }

    return true;
};

/**
 * Functional guard para System Admins
 */
export const systemAdminGuard = () => {
    const authService = inject(AuthService);
    const rolesService = inject(RolesService);
    const router = inject(Router);

    if (!authService.estaAutenticado()) {
        router.navigate(['/auth/login']);
        return false;
    }

    if (!rolesService.hasRole(Roles.SYSTEM_ADMIN)) {
        router.navigate(['/unauthorized']);
        return false;
    }

    return true;
};

/**
 * Functional guard para gerenciamento de usuários
 */
export const userManagementGuard = () => {
    const authService = inject(AuthService);
    const rolesService = inject(RolesService);
    const router = inject(Router);

    if (!authService.estaAutenticado()) {
        router.navigate(['/auth/login']);
        return false;
    }

    if (!rolesService.canManageUsers()) {
        router.navigate(['/unauthorized']);
        return false;
    }

    return true;
};

/**
 * Functional guard para relatórios
 */
export const reportsGuard = () => {
    const authService = inject(AuthService);
    const rolesService = inject(RolesService);
    const router = inject(Router);

    if (!authService.estaAutenticado()) {
        router.navigate(['/auth/login']);
        return false;
    }

    if (!rolesService.canViewReports()) {
        router.navigate(['/unauthorized']);
        return false;
    }

    return true;
};

/**
 * Functional guard para features específicas
 */
export const featureGuard = (feature: string) => {
    return () => {
        const authService = inject(AuthService);
        const rolesService = inject(RolesService);
        const router = inject(Router);

        if (!authService.estaAutenticado()) {
            router.navigate(['/auth/login']);
            return false;
        }

        if (!rolesService.canAccessFeature(feature)) {
            router.navigate(['/unauthorized']);
            return false;
        }

        return true;
    };
};

/**
 * Functional guard para roles específicos
 */
export const roleGuard = (roles: Roles | Roles[]) => {
    return () => {
        const authService = inject(AuthService);
        const rolesService = inject(RolesService);
        const router = inject(Router);

        if (!authService.estaAutenticado()) {
            router.navigate(['/auth/login']);
            return false;
        }

        const requiredRoles = Array.isArray(roles) ? roles : [roles];
        if (!rolesService.hasAnyRole(requiredRoles)) {
            router.navigate(['/unauthorized']);
            return false;
        }

        return true;
    };
};
